/**
 * Log Call Modal
 *
 * Modal for manually logging phone calls on record detail pages.
 * Creates an Activity record when call is logged.
 */

import React, { useState } from 'react';
import { X, Phone } from 'lucide-react';
import { activityStorage } from '../../services/activityStorage';
import { RecordType, CallOutcome, CallDirection } from '../../types/activity';
import { useToast } from '../../contexts/ToastContext';
import { useCurrentUser } from '../../hooks/useCurrentUser';
import { TIMEZONE_OPTIONS, detectLocalTimezone } from '../common/TimeWithLocalHover';

interface LogCallModalProps {
  isOpen: boolean;
  onClose: () => void;
  recordId: string;
  recordType: RecordType;
  contactName?: string;
  phoneNumber?: string;
  onCallLogged?: () => void;
}

const callOutcomes: { value: CallOutcome; label: string }[] = [
  { value: 'connected', label: 'Connected' },
  { value: 'voicemail', label: 'Left Voicemail' },
  { value: 'no_answer', label: 'No Answer' },
  { value: 'busy', label: 'Busy' },
  { value: 'wrong_number', label: 'Wrong Number' },
];

export const LogCallModal: React.FC<LogCallModalProps> = ({
  isOpen,
  onClose,
  recordId,
  recordType,
  contactName,
  phoneNumber,
  onCallLogged,
}) => {
  const { success } = useToast();
  const { userId, userName } = useCurrentUser();
  const [direction, setDirection] = useState<CallDirection>('outbound');
  const [outcome, setOutcome] = useState<CallOutcome | ''>('');
  const [duration, setDuration] = useState<string>('');
  const [subject, setSubject] = useState<string>('');
  const [notes, setNotes] = useState<string>('');
  const [timezone, setTimezone] = useState(detectLocalTimezone);
  const [error, setError] = useState<string>('');

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (!outcome) {
      setError('Please select a call outcome');
      return;
    }

    try {
      // Create activity record (async for Gobodo API)
      await activityStorage.create({
        type: 'call',
        recordId,
        recordType,
        userId,
        userName,
        subject: subject || `${direction === 'outbound' ? 'Outbound' : 'Inbound'} Call`,
        description: notes,
        duration: duration ? parseInt(duration, 10) : undefined,
        direction,
        outcome: outcome as CallOutcome,
        recipientPhone: phoneNumber,
        recipientName: contactName,
        source: 'manual',
        timezone,
      });

      // Reset form
      setDirection('outbound');
      setOutcome('');
      setDuration('');
      setSubject('');
      setNotes('');
      setTimezone(detectLocalTimezone());
      setError('');

      success('Call logged', `Call with ${contactName || 'contact'} has been logged`);

      // Callback to refresh activity list
      onCallLogged?.();

      // Close modal
      onClose();
    } catch (err) {
      console.error('[LogCallModal] Error logging call:', err);
      setError('Failed to log call. Please try again.');
    }
  };

  const handleClose = () => {
    setError('');
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Log Call
            </h2>
            <button
              onClick={handleClose}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          {/* Content */}
          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            {/* Contact Info */}
            {(contactName || phoneNumber) && (
              <div className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <Phone className="w-5 h-5 text-gray-400" />
                <div>
                  {contactName && (
                    <p className="font-medium text-gray-900 dark:text-white">{contactName}</p>
                  )}
                  {phoneNumber && (
                    <p className="text-sm text-gray-500 dark:text-gray-400">{phoneNumber}</p>
                  )}
                </div>
              </div>
            )}

            {/* Direction */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Direction
              </label>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="direction"
                    value="outbound"
                    checked={direction === 'outbound'}
                    onChange={(e) => setDirection(e.target.value as CallDirection)}
                    className="w-4 h-4 text-blue-600"
                  />
                  <span className="text-gray-700 dark:text-gray-300">Outbound</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="direction"
                    value="inbound"
                    checked={direction === 'inbound'}
                    onChange={(e) => setDirection(e.target.value as CallDirection)}
                    className="w-4 h-4 text-blue-600"
                  />
                  <span className="text-gray-700 dark:text-gray-300">Inbound</span>
                </label>
              </div>
            </div>

            {/* Outcome */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Call Outcome <span className="text-red-500">*</span>
              </label>
              <select
                value={outcome}
                onChange={(e) => {
                  setOutcome(e.target.value as CallOutcome);
                  setError('');
                }}
                className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Select outcome...</option>
                {callOutcomes.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              {error && (
                <p className="mt-1 text-sm text-red-500">{error}</p>
              )}
            </div>

            {/* Duration & Timezone Row */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Duration (minutes)
                </label>
                <input
                  type="number"
                  min="0"
                  value={duration}
                  onChange={(e) => setDuration(e.target.value)}
                  placeholder="e.g., 15"
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Timezone
                </label>
                <select
                  value={timezone}
                  onChange={(e) => setTimezone(e.target.value)}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  {TIMEZONE_OPTIONS.map((tz) => (
                    <option key={tz.value} value={tz.value}>
                      {tz.label} ({tz.abbr})
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Subject */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Subject (optional)
              </label>
              <input
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="e.g., Discovery Call"
                className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Notes
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={4}
                placeholder="What was discussed..."
                className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
              />
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-4">
              <button
                type="button"
                onClick={handleClose}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
              >
                Log Call
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
